import { type ServiceParams, unwrapJson } from "../client";
import type {
  AssetBaseResponse,
  AssetChangeListResponse,
  AssetCreatePayload,
  AssetDetailResponse,
  AssetListFilters,
  AssetListResponse,
  AssetTransitionPayload,
  AssetType,
  AssetUpdatePayload,
} from "./types";

/**
 * Asset inventory service. The backend exposes a typed-create
 * pattern: ``POST /v1/governance/assets/{type}`` where ``type`` is
 * one of seven enum values. Updates, deletes, lifecycle
 * transitions, and history use the un-typed ``/{asset_id}``
 * suffix.
 */
const Assets = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/governance/assets`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    async list(filters: AssetListFilters = {}): Promise<AssetListResponse> {
      const response = await fetch(`${base}${buildQuery(filters as Record<string, unknown>)}`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<AssetListResponse>(response);
    },

    async get(assetId: string): Promise<AssetDetailResponse> {
      const response = await fetch(`${base}/${assetId}`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<AssetDetailResponse>(response);
    },

    async create(
      assetType: AssetType,
      body: AssetCreatePayload
    ): Promise<AssetBaseResponse> {
      // Backend pluralises every type's path segment by appending an
      // 's': prompt -> /prompts, model -> /models, etc. We codify
      // that here so the dialog layer doesn't have to know.
      const path = `${assetType}s`;
      const response = await fetch(`${base}/${path}`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<AssetBaseResponse>(response);
    },

    async update(
      assetId: string,
      body: AssetUpdatePayload
    ): Promise<AssetBaseResponse> {
      const response = await fetch(`${base}/${assetId}`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<AssetBaseResponse>(response);
    },

    async transition(
      assetId: string,
      body: AssetTransitionPayload
    ): Promise<AssetBaseResponse> {
      const response = await fetch(`${base}/${assetId}/transition`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<AssetBaseResponse>(response);
    },

    async delete(assetId: string): Promise<AssetBaseResponse> {
      const response = await fetch(`${base}/${assetId}`, {
        method: "DELETE",
        headers: config(),
      });
      return unwrapJson<AssetBaseResponse>(response);
    },

    async getHistory(
      assetId: string,
      filters: { limit?: number; offset?: number } = {}
    ): Promise<AssetChangeListResponse> {
      const response = await fetch(
        `${base}/${assetId}/history${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<AssetChangeListResponse>(response);
    },
  };
};

export default Assets;
